<script setup lang="ts">
const enableDeveloperDebug = localStorage.getItem("developerDebug") === "true"; // Set to true to enable developer debug logs

if (enableDeveloperDebug) {
  console.log("Developer debug mode enabled - WorkflowResponseViewer.vue");
}

import {
  ArrowLeftBold,
  ArrowRightBold,
  CircleCheck,
  Download,
  Edit,
} from "@element-plus/icons-vue";
import { HubConnection } from "@microsoft/signalr";
import { useStorage } from "@vueuse/core";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import {
  Entity,
  EntityFormInWorkflowDefinition,
  FormAnswerUpdatedDto,
  FormDefinition,
  FormResponse,
  FormResponseDto,
  FormResponseStatus,
  FormType,
  LabelledOption,
  ListDefinition,
  SysOrgRoleName,
  Workflow,
  type WorkflowContentDto,
  WorkflowDto,
} from "~/api";
import { useFormResponse } from "~/NewFormBuilder/composables/useFormResponse";
import {
  buildVersionInfo,
  compareFormVersions,
  getFormVersionKey,
  siblingVersionsOf,
} from "~/NewFormBuilder/composables/useFormVersions";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { getEnvConfig } from "~/services/appConfig";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import type { Slotted } from "~/types/Slotted";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { parseDate } from "~/utils/dateUtils";
import { formTypeDescriptions } from "~/utils/enumDescriptions";
import { formResponseStatusDisplayNames } from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import { getMessageTime } from "~/utils/miscUtils";
import { useWorkflow, workflowInjectKey } from "../composables/useWorkflow";
import FormResponseTemplate from "../pages/FormResponseTemplate.vue";
import EntityResponseViewer from "./EntityResponseViewer.vue";
import WorkflowResponseFormViewer from "./WorkflowResponseFormViewer.vue";
// Use Slotted in a type alias so the import is considered used by the linter.
type _SlottedUsage = Slotted<FormResponseDto> | Slotted<FormDefinition>;
// ...existing code...

const myInfoStore = useMyInfoStore();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updateWorkflow", workflow: WorkflowDto): void;
  (e: "refreshList"): void;
  (e: "openFollowUpWorkflow", workflowGuid: string): void;
  (e: "loaded"): void;
}>();

const props = defineProps<{
  complianceWorkflow: WorkflowDto;
  fullScreen: boolean;
  inline?: boolean;
  hideCompareToggle?: boolean;
  // Optional: when provided, focus the first form matching this definition name
  focusFormResponseGuid?: string | null;
  readOnly?: boolean;
  // Optional: per-form display mode overrides keyed by form code (e.g., "PRS")
  formDisplayModeOverrides?: Record<string, FormDisplayMode>;
  /**
   * Optional alternate source for the workflow content. When provided, it replaces the
   * standard workflow API fetch — e.g. content served from another system's database. The
   * viewer is then forced read-only: its save/status/delete calls only know the standard
   * workflow API, so editing content that did not come from there would write to the wrong
   * place.
   */
  contentLoader?: () => Promise<WorkflowContentDto>;
}>();
const editingWorkflowStatus = ref(false);
const workflow = ref<Workflow>();
const formDrawerOpen = ref(false);

// Time frame date override functionality
const timeFrameOverrideDate = ref<string | undefined>();
const isDevEnvironment = computed(() => {
  const env = getEnvConfig().env;
  return env === "development" || env === "sunweb";
});
const hasTimeFrames = computed(() => {
  return (workflow.value?.workflowDefinition?.timeFrames?.length ?? 0) > 0;
});

// Initialize date override from localStorage
const initializeDateOverride = () => {
  if (isDevEnvironment.value) {
    const stored = localStorage.getItem("timeFrameOverrideDate");
    if (stored) {
      timeFrameOverrideDate.value = stored;
    }
  }
};

const handleViewForm = (form: FormResponseDto) => {
  const matchingFormResponse =
    workflow.value?.workflowDefinition?.formDefinitions?.find(
      (definition) => definition.guid === form.formDefinitionGuid,
    );

  if (!form.guid || !matchingFormResponse) {
    showError("No form found.");
    return;
  }
  formDrawerOpen.value = true;
  workflowFormBeingViewed.value = {
    formDefinition: matchingFormResponse,
    formResponseDto: form,
  };
};

const drawerOpen = ref(false);
const entityDrawerOpen = ref(false);

const pdfLoadingStore = usePdfLoadingStore();

const statusOptions = ref<{ code: string; label: string }[]>([]);
const editing = ref(false);
const isArchived = computed(() =>
  workflow.value?.archivedAt ? !!parseDate(workflow.value?.archivedAt) : false,
);
const isEffectivelyReadOnly = computed(
  () => isArchived.value || !!props.readOnly || !!props.contentLoader,
);

// Form codes are admin-editable free text, so match them case-insensitively
// like the rest of the codebase (findFormDefByCode, getWorkflowFormVersionKey)
const normalizedDisplayModeOverrides = computed<
  Record<string, FormDisplayMode>
>(() => {
  const normalized: Record<string, FormDisplayMode> = {};
  for (const [code, mode] of Object.entries(
    props.formDisplayModeOverrides ?? {},
  )) {
    normalized[code.trim().toUpperCase()] = mode;
  }
  return normalized;
});

const getFormDisplayMode = (form: FormResponse): FormDisplayMode => {
  if (isEffectivelyReadOnly.value) return FormDisplayMode.READ_ONLY;
  // A superseded (previous-version) response is the frozen "original" — always read-only.
  if (isSupersededResponse(form)) return FormDisplayMode.READ_ONLY;

  const formCode =
    form.formDefinition?.formCode ??
    workflow.value?.workflowDefinition?.formDefinitions?.find(
      (fd) => fd.guid === form.formDefinitionGuid,
    )?.formCode;

  const override = formCode
    ? normalizedDisplayModeOverrides.value[formCode.trim().toUpperCase()]
    : undefined;
  return override ?? FormDisplayMode.EDIT;
};

const sharedAnswers = ref<Record<string, string>>({});
const workflowLists = ref<ListDefinition[]>([]);

const pdfCodes = computed((): LabelledOption[] => {
  return workflowLists.value
    .filter((list) => list.specialList === "PdfCode")
    .flatMap((list) => {
      list.items.unshift({ value: "", label: "(main)" });
      return list.items;
    });
});
const selectedPDF = useStorage("selectedPDF", "");

// View mode state
const viewMode = ref<"stacked" | "compare">("stacked");

// Compare mode state — max 2 forms shown side-by-side
const selectedFormGuids = ref<string[]>([]);
const compareModeSidebarCollapsed = ref(false);

const MAX_COMPARE_FORMS = 2;

const sharedAnswersForProps = computed(() => sharedAnswers.value);

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

const showEntityName = false; // may want to show entity name

// Only the user who created the workflow or users in a group that can see all these workflows
// can edit the workflow name
const canEditWorkflowName = computed(() => {
  const myOrgInfo = myInfoStore.currentSysOrg;
  if (!myOrgInfo || !workflow.value) {
    return false;
  }
  return (
    workflow.value?.entityGuid === myOrgInfo.entityDto?.guid ||
    (workflow.value?.workflowDefinition?.groupsWithAllAccess?.some((group) =>
      myOrgInfo.groupGuids.includes(group),
    ) ??
      false)
  );
});

// When a form is upgraded, a new response is created on the new version while the old response
// is kept as the read-only "original". Within one template version-group, the response on the
// newest version is the active one; older siblings are "superseded" and hidden unless the user
// opts to view previous versions.
const supersededResponseGuids = computed<Set<string>>(() => {
  const stepResponses =
    workflow.value?.formResponses?.filter(
      (f) => f.formType === FormType.WORKFLOW_STEP && !!f.formDefinition,
    ) ?? [];

  const byTemplate = new Map<string, FormResponse[]>();
  for (const resp of stepResponses) {
    const key = getFormVersionKey(resp.formDefinition!);
    const list = byTemplate.get(key);
    if (list) list.push(resp);
    else byTemplate.set(key, [resp]);
  }

  const superseded = new Set<string>();
  for (const group of byTemplate.values()) {
    // Only a genuine multi-version situation counts: the group must span at least two DISTINCT
    // form definitions (an actual upgrade). A single-version form is never "previous version",
    // even if it has multiple response instances.
    const distinctDefs = new Set(group.map((r) => r.formDefinitionGuid));
    if (distinctDefs.size < 2) continue;
    // Newest version wins (last after ascending sort by the same compareFormVersions used for the
    // v1/v2 labels); the rest are superseded.
    const sorted = [...group].sort((a, b) =>
      compareFormVersions(a.formDefinition!, b.formDefinition!),
    );
    const active = sorted[sorted.length - 1];
    for (const resp of sorted) {
      if (resp.guid && resp.guid !== active.guid) superseded.add(resp.guid);
    }
  }
  return superseded;
});

// Whether a response is an older version superseded by a newer one in this workflow. Uses the
// client-side version grouping (same compareFormVersions as the v1/v2 version labels) as the
// single source of truth, so what's hidden/tagged always matches the version numbers shown.
const isSupersededResponse = (form: FormResponse): boolean =>
  !!form.guid && supersededResponseGuids.value.has(form.guid);

// User toggle to reveal superseded (previous-version) responses, which are otherwise hidden.
const showPreviousVersions = ref(false);

// How many workflow-step responses are superseded (drives the "Show previous versions" toggle).
const supersededResponseCount = computed<number>(
  () =>
    workflow.value?.formResponses?.filter(
      (f) => f.formType === FormType.WORKFLOW_STEP && isSupersededResponse(f),
    ).length ?? 0,
);

const workflowStepForms = computed<FormResponse[]>(() => {
  return (
    workflow.value?.formResponses
      ?.filter((f) => f.formType === FormType.WORKFLOW_STEP)
      // Hide superseded (previous-version) responses unless the user opts to view them.
      .filter((f) => showPreviousVersions.value || !isSupersededResponse(f))
      .sort(
        (a, b) =>
          (a.formDefinition?.sortOrder ?? 0) -
          (b.formDefinition?.sortOrder ?? 0),
      ) ?? []
  );
});

const workflowLegacyForms = computed<FormResponse[]>(() => {
  return (
    workflow.value?.formResponses?.filter(
      (f) =>
        f.formType !== FormType.WORKFLOW_STEP &&
        f.formType !== FormType.WORKFLOW_DEFINITION_MAIN_FORM &&
        f.formType !== FormType.ENTITY_DEFINITION &&
        f.formType !== FormType.CONFIGURED_FORM,
    ) || []
  );
});

/**
 * Entity form responses: form responses whose formType is EntityDefinition,
 * meaning they are entity definition forms included in this workflow via
 * EntityFormsInWorkflowDefinition configuration.
 */
const entityFormResponses = computed<FormResponse[]>(() => {
  return (
    workflow.value?.formResponses?.filter(
      (f) => f.formType === FormType.ENTITY_DEFINITION,
    ) ?? []
  );
});

/**
 * Get the EntityFormInWorkflowDefinition config for a given entity form response,
 * so we can display metadata and trigger entity creation.
 * Uses EntityFormInWorkflowDefinitionGuid for precise matching (supports multiple
 * forms of the same entity type), falls back to FormDefinitionGuid matching.
 */
const getEntityFormConfig = (
  formResponse: FormResponse,
): EntityFormInWorkflowDefinition | undefined => {
  const configs =
    workflow.value?.workflowDefinition?.entityFormsInWorkflowDefinition;
  if (!configs) {
    return undefined;
  }

  // Precise match via the linking guid (supports multiple of same type)
  if (formResponse.entityFormInWorkflowDefinitionGuid) {
    return configs.find(
      (ef) => ef.guid === formResponse.entityFormInWorkflowDefinitionGuid,
    );
  }

  // Fallback: match by FormDefinitionGuid (backwards compatibility)
  return configs.find(
    (ef) =>
      ef.entityDefinition?.formDefinitionGuid ===
      formResponse.formDefinitionGuid,
  );
};

const creatingEntityFromForm = ref<string | null>(null);

const handleCreateEntityFromForm = async (formResponse: FormResponse) => {
  if (!workflow.value?.guid || !formResponse.guid) {
    return;
  }

  creatingEntityFromForm.value = formResponse.guid;
  try {
    const entity =
      await apiClient.workflowInstance.createEntityFromWorkflowForm(
        workflow.value.guid,
        formResponse.guid,
      );
    ElMessage.success(
      `Entity "${entity.name ?? "New Entity"}" created successfully`,
    );
    // Refresh the workflow to show the linked entity
    await showWorkflow(props.complianceWorkflow);
  } catch (error) {
    showError(error);
  } finally {
    creatingEntityFromForm.value = null;
  }
};

const generalForms = computed<FormResponse[]>(
  () =>
    workflow.value?.formResponses?.filter(
      (f) => f.formType === FormType.WORKFLOW_DEFINITION_MAIN_FORM,
    ) ?? [],
);

// Version key + ordering now live in the shared useFormVersions composable so the workflow
// viewer, the forms tab, and the designer all agree. Local aliases keep call sites tidy.
const getWorkflowFormVersionKey = getFormVersionKey;
const compareWorkflowFormVersions = compareFormVersions;

const applicableWorkflowFormDefinitions = computed<FormDefinition[]>(() => {
  const formDefinitions =
    workflow.value?.workflowDefinition?.formDefinitions ?? [];
  const workflowCreatedAt = workflow.value?.createdAt
    ? Date.parse(workflow.value.createdAt)
    : Number.NaN;

  const selectedByKey = new Map<string, FormDefinition>();
  for (const formDefinition of formDefinitions) {
    if (
      Number.isFinite(workflowCreatedAt) &&
      formDefinition.createdAt &&
      Date.parse(formDefinition.createdAt) > workflowCreatedAt
    ) {
      continue;
    }

    const key = getWorkflowFormVersionKey(formDefinition);
    const current = selectedByKey.get(key);
    const isCandidateDefault = !!formDefinition.metaInfo?.defaultForFormCode;
    const isCurrentDefault = !!current?.metaInfo?.defaultForFormCode;
    if (!current) {
      selectedByKey.set(key, formDefinition);
    } else if (isCandidateDefault && !isCurrentDefault) {
      // Prefer explicitly marked default over non-default (even if older)
      selectedByKey.set(key, formDefinition);
    } else if (!isCandidateDefault && isCurrentDefault) {
      // keep the current default
    } else if (compareWorkflowFormVersions(formDefinition, current) > 0) {
      selectedByKey.set(key, formDefinition);
    }
  }

  return formDefinitions.filter(
    (formDefinition) =>
      selectedByKey.get(getWorkflowFormVersionKey(formDefinition))?.guid ===
      formDefinition.guid,
  );
});

const creatableForms = computed(() => {
  const defs = workflow.value?.workflowDefinition?.formDefinitions ?? [];
  const responses = workflow.value?.formResponses ?? [];
  const responseCountByKey = new Map<string, number>();

  responses.forEach((response) => {
    const responseDefinition = defs.find(
      (fd) => fd.guid === response.formDefinitionGuid,
    );
    if (!responseDefinition) {
      return;
    }

    const key = getWorkflowFormVersionKey(responseDefinition);
    responseCountByKey.set(key, (responseCountByKey.get(key) ?? 0) + 1);
  });

  return applicableWorkflowFormDefinitions.value
    .filter((fd) => fd.metaInfo?.userCreated)
    .map((fd) => {
      const key = getWorkflowFormVersionKey(fd);
      const existingCount = responseCountByKey.get(key) ?? 0;
      const maxInstances = fd.metaInfo?.multipleInstancesAllowed
        ? fd.metaInfo?.maxInstances || 0
        : 1;
      return {
        formDefinition: fd,
        existingCount,
        maxInstances,
        canCreate: maxInstances === 0 || existingCount < maxInstances,
      };
    });
});

const creatingForm = ref<string | null>(null);

const handleCreateFormInWorkflow = async (formDef: FormDefinition) => {
  if (!workflow.value?.guid || !formDef.guid) {
    return;
  }
  creatingForm.value = formDef.guid;
  try {
    await apiClient.workflowInstance.createFormResponseInWorkflow(
      workflow.value.guid,
      formDef.guid,
    );
    if (props.complianceWorkflow) {
      await showWorkflow(props.complianceWorkflow);
    }
  } catch (e) {
    showError(e);
  } finally {
    creatingForm.value = null;
  }
};

const isSingleForm = computed<boolean>(
  () => workflowStepForms.value.length === 1,
);

const shouldHideWorkflowStatus = computed<boolean>(() => {
  return (
    isSingleForm.value &&
    workflowStepForms.value.length > 0 &&
    shouldShowFormStatus(workflowStepForms.value[0])
  );
});

const isMobile = computed(() => {
  return window.innerWidth < 768;
});

// All forms that can appear in the sidebar / be selected for compare
const allComparableForms = computed<FormResponse[]>(() => {
  return [
    ...entityFormResponses.value,
    ...workflowLegacyForms.value,
    ...workflowStepForms.value,
  ].filter((form) => form.guid);
});

// In compare mode: show only the (up to 2) selected forms
const selectedForms = computed<FormResponse[]>(() => {
  return allComparableForms.value.filter((form) =>
    selectedFormGuids.value.includes(form.guid || ""),
  );
});

const shouldShowCompareMode = computed(() => {
  return allComparableForms.value.length >= 2 && !props.hideCompareToggle;
});

const workflowFormBeingViewed = ref<{
  formDefinition: FormDefinition;
  formResponseDto: FormResponseDto;
}>();

const getFormName = (formDefinitionGuid: string) => {
  return (
    workflow.value?.workflowDefinition?.formDefinitions?.find(
      (f) => f.guid === formDefinitionGuid,
    )?.name ?? "Unknown Form"
  );
};

const activeSteps = ref<(number | string)[]>([]);

// Interface for comprehensive collapse state tracking
interface CollapseStateEntry {
  formGuid: string;
  formName: string;
  isOpen: boolean;
  index: number; // 1-based index for el-collapse
}

interface CollapseState {
  version: number; // for future compatibility
  entries: CollapseStateEntry[];
  lastUpdated: number; // timestamp
}

// SessionStorage key for remembering collapse state
const getCollapseStateKey = (workflowGuid: string) =>
  `workflow-collapse-state-${workflowGuid}`;

// Load collapse state from sessionStorage
const loadCollapseState = (workflowGuid: string): CollapseState | null => {
  try {
    const saved = sessionStorage.getItem(getCollapseStateKey(workflowGuid));
    if (!saved) {
      return null;
    }

    const parsed = JSON.parse(saved);

    // Check if it's the new format
    if (
      parsed &&
      typeof parsed === "object" &&
      "version" in parsed &&
      "entries" in parsed
    ) {
      return parsed as CollapseState;
    }

    // If it's old format or invalid format, ignore it
    return null;
  } catch (error) {
    console.warn("Failed to load collapse state:", error);
    return null;
  }
};

// Save collapse state to sessionStorage in new comprehensive format
const saveCollapseState = (
  workflowGuid: string,
  activeStepIndices: (number | string)[],
) => {
  try {
    const entries: CollapseStateEntry[] = workflowStepForms.value.map(
      (step, index) => ({
        formGuid: step.guid || "",
        formName: step.formDefinition?.name || "Unknown Form",
        // activeStepIndices may contain numeric indices (1-based), their string
        // representations, or form GUIDs (strings). Consider a step open if any
        // of those are present.
        isOpen:
          activeStepIndices.includes(index + 1) ||
          activeStepIndices.includes(String(index + 1)) ||
          (step.guid ? activeStepIndices.includes(step.guid) : false),
        index: index + 1,
      }),
    );

    // If activeStepIndices contains GUIDs that are not yet present in
    // workflowStepForms (for example when props.focusFormResponseGuid is
    // added before the forms list is populated), persist them too so the
    // focused form remains expanded across refreshes.
    const existingGuids = new Set(
      entries.map((e) => e.formGuid).filter(Boolean),
    );
    activeStepIndices.forEach((asi) => {
      if (typeof asi === "string") {
        // Skip numeric indices represented as strings
        if (/^\d+$/.test(asi)) {
          return;
        }
        if (!existingGuids.has(asi)) {
          entries.push({
            formGuid: asi,
            formName: "Unknown Form",
            isOpen: true,
            index: 0,
          });
          existingGuids.add(asi);
        }
      }
    });

    const state: CollapseState = {
      version: 1,
      entries,
      lastUpdated: Date.now(),
    };

    sessionStorage.setItem(
      getCollapseStateKey(workflowGuid),
      JSON.stringify(state),
    );
  } catch (error) {
    console.warn("Failed to save collapse state:", error);
  }
};

// Watch for changes to activeSteps and save to sessionStorage
watch(
  activeSteps,
  (newActiveSteps) => {
    if (workflow.value?.guid && workflowStepForms.value.length > 0) {
      saveCollapseState(workflow.value.guid, newActiveSteps);
    }
  },
  { deep: true },
);

// When entering compare mode, auto-expand selected forms
watch(viewMode, (newMode) => {
  // When entering compare mode, auto-select first 2 forms if none selected
  if (newMode === "compare" && selectedFormGuids.value.length === 0) {
    autoSelectDefaultForms();
  }

  // Ensure compare mode forms are expanded (collapse uses form GUIDs as names)
  if (newMode === "compare") {
    const guidsToExpand = selectedFormGuids.value.filter(
      (guid) => !activeSteps.value.includes(guid),
    );
    if (guidsToExpand.length > 0) {
      activeSteps.value = [...activeSteps.value, ...guidsToExpand];
    }
  }
});

// Determines which steps should be open based on saved state and current forms
const determineStepsToOpen = (currentWorkflow?: Workflow): number[] => {
  if (!currentWorkflow) {
    return [];
  }

  const currentForms =
    currentWorkflow.formResponses
      ?.filter((f) => f.formType === FormType.WORKFLOW_STEP)
      .sort(
        (a, b) =>
          (a.formDefinition?.sortOrder ?? 0) -
          (b.formDefinition?.sortOrder ?? 0),
      ) ?? [];

  const singleForm = currentForms.length === 1;

  // Try to load collapse state from sessionStorage first
  const savedState = currentWorkflow.guid
    ? loadCollapseState(currentWorkflow.guid)
    : null;

  // Determine which steps should be open
  let stepsToOpen: number[] = [];

  if (singleForm) {
    stepsToOpen = [1];
  } else if (savedState) {
    // Handle new comprehensive format
    const savedEntries = savedState.entries;

    // Create a map for quick lookup of saved states by form GUID
    const savedStateMap = new Map<string, boolean>();
    savedEntries.forEach((entry) => {
      if (entry.formGuid) {
        savedStateMap.set(entry.formGuid, entry.isOpen);
      }
    });

    // Process each current form
    stepsToOpen = currentForms
      .map((step, index) => {
        const stepIndex = index + 1;

        if (step.guid && savedStateMap.has(step.guid)) {
          // Form exists in saved state - use saved open/closed status
          return savedStateMap.get(step.guid) ? stepIndex : null;
        } else {
          // New form not in saved state - default to open and it will be saved automatically
          return stepIndex;
        }
      })
      .filter((stepIndex) => stepIndex !== null);
  } else {
    // No saved state - use fallback logic based on displayOnStatus
    stepsToOpen = currentForms
      .map((step, index) => {
        const matchingStatus =
          step.formDefinition?.metaInfo.displayOnStatus?.includes(
            currentWorkflow.status ?? "",
          );
        return matchingStatus ? index + 1 : null;
      })
      .filter((stepIndex) => stepIndex !== null);
  }

  return stepsToOpen;
};

const showWorkflow = async (workflowInstanceDto: WorkflowDto) => {
  drawerOpen.value = true;
  if (!workflowInstanceDto.guid) {
    showError("Workflow instance not found");
    return;
  }
  // The caller may supply its own content source (see the contentLoader prop); otherwise
  // content comes from the standard workflow API.
  const contentRequest = props.contentLoader
    ? props.contentLoader()
    : apiClient.workflowInstance.getWorkflow(workflowInstanceDto.guid);
  await contentRequest
    .then((dto) => {
      sharedAnswers.value = dto.sharedAnswers;
      workflowLists.value = dto.listDefinitions;
      workflow.value = extend(dto.workflow);

      // Determine which steps should be open after workflow refresh
      // This ensures new forms that become visible are expanded
      const stepsToOpen = determineStepsToOpen(workflow.value);

      if (enableDeveloperDebug) {
        console.log("Steps to open after workflow refresh:", stepsToOpen);
      }

      // Also include form GUIDs so compare mode collapse items (keyed by guid) expand correctly.
      // stepsToOpen contains 1-based indices into workflowStepForms; map those to GUIDs.
      const sortedStepForms =
        workflow.value?.formResponses
          ?.filter((f) => f.formType === FormType.WORKFLOW_STEP)
          .sort(
            (a, b) =>
              (a.formDefinition?.sortOrder ?? 0) -
              (b.formDefinition?.sortOrder ?? 0),
          ) ?? [];
      const guidSteps = stepsToOpen
        .map((idx) => sortedStepForms[idx - 1]?.guid)
        .filter((g): g is string => !!g);

      activeSteps.value = [...stepsToOpen, ...guidSteps];
    })
    .catch(showError);
};

const extend = (wf: Workflow): Workflow | undefined => {
  if (!wf) {
    return undefined;
  }
  // copy formDefinitions to workflows
  for (const formResponse of wf.formResponses ?? []) {
    const matchingFormDefinition = wf.workflowDefinition?.formDefinitions?.find(
      (fd) => fd.guid === formResponse.formDefinitionGuid,
    );
    if (matchingFormDefinition) {
      formResponse.formDefinition = matchingFormDefinition;
      formResponse.formType = matchingFormDefinition.formType;
    } else {
      console.warn(
        `No matching form definition found for form response ${formResponse.guid}`,
      );
    }
  }
  return wf;
};

const updateWorkflow = async () => {
  if (!workflow.value?.guid) {
    showError("Workflow instance not found");
    return;
  }
  if (!workflow.value?.status) {
    showError("Status not found");
    return;
  }
  await apiClient.workflowInstance
    .updateWorkflow(workflow.value.guid, workflow.value)
    .then((res) => {
      ElMessage.info("Workflow updated");
      editing.value = false;

      emit("updateWorkflow", res);
    })
    .catch(showError);
};

const handleClose = async () => {
  emit("closeDrawer");
};

const isCancelable = computed(() => {
  const cancelStatus = workflow.value?.workflowDefinition?.cancelableOnStatus;
  if (!cancelStatus) {
    return false;
  }
  return workflow.value?.status === cancelStatus;
});

const cancelledRef = ref(false);

const handleCancelWorkflow = async (): Promise<boolean> => {
  try {
    const wfName =
      workflow.value?.name ||
      workflow.value?.workflowDefinition?.name ||
      "this form";
    await ElMessageBox.confirm(
      `This will cancel and permanently delete "${wfName}". Continue?`,
      "Cancel",
      {
        confirmButtonText: "Yes, Cancel",
        cancelButtonText: "No, Go Back",
        type: "warning",
      },
    );
    if (!workflow.value?.guid) {
      ElMessage.error("Workflow instance not found");
      return false;
    }
    await apiClient.workflowInstance.deleteWorkflowInstance(
      workflow.value.guid,
    );
    ElMessage.success("Cancelled");
    emit("refreshList");
    cancelledRef.value = true;
    drawerOpen.value = false;
    return true;
  } catch (error) {
    if (error === "cancel" || error === "close") {
      return false;
    }
    showError(error);
    return false;
  }
};

// Handle time frame date override
const handleDateOverrideChange = (newDate: string | undefined) => {
  if (isDevEnvironment.value) {
    if (newDate) {
      localStorage.setItem("timeFrameOverrideDate", newDate);
      timeFrameOverrideDate.value = newDate;
      ElMessage.success(
        `Time frame date overridden to: ${new Date(newDate).toDateString()}`,
      );
    } else {
      localStorage.removeItem("timeFrameOverrideDate");
      timeFrameOverrideDate.value = undefined;
      ElMessage.success("Time frame date override cleared");
    }
  }
};

watch(timeFrameOverrideDate, (newValue) => {
  if (newValue) {
    handleDateOverrideChange(newValue);
  }
});

const clearDateOverride = () => {
  handleDateOverrideChange(undefined);
};

const handleClosing = async (done: (cancel?: boolean) => void) => {
  // Skip confirmation if workflow was already cancelled (avoids double-trigger)
  if (cancelledRef.value) {
    cancelledRef.value = false;
    done();
    return;
  }
  if (isCancelable.value) {
    const cancelled = await handleCancelWorkflow();
    done(cancelled ? undefined : true);
    return;
  }
  done(props.fullScreen); // don't allow closing when in full screen
};

// Stable handler references for the shared "workflowInstance" hub.
// (PdfMsg on "self" is managed inside pdfLoadingStore.makePdf() for reliable delivery of step 2 / completion messages.)
const workflowMsgHandler = (obj: any) => {
  console.log("Message received:", obj);
  if (obj.success) {
    ElMessage.info({
      message: obj.msg,
      duration: getMessageTime(obj.msg),
      showClose: true,
    });
  } else {
    showError(obj.msg);
  }
};

/**
 * Re-encode a "cleaned" answer value (as broadcast over SignalR) back into the
 * JSON-stringified form that the rest of the form code expects for stored
 * answers.
 *
 * The server strips the outer quotes from JSON string answers before
 * broadcasting (TryDeserializeJsonString), but leaves array/object/scalar
 * answers as raw JSON. We can't reliably infer the original type from the
 * cleaned value alone (a text answer of "true" or "123" looks like a JSON
 * boolean/number), so we key off the shape of the *existing* stored answer,
 * which is always valid JSON:
 *  - starts with `"`  → string answer; re-quote the cleaned value.
 *  - anything else    → array/object (list answers) or bare scalar
 *                       (number/bool/null); the broadcast value is already
 *                       in the right JSON shape, so keep it as-is.
 */
const reEncodeSharedAnswerValue = (
  cleanedValue: string,
  existingValue: string,
): string => {
  return existingValue.trimStart().startsWith('"')
    ? JSON.stringify(cleanedValue)
    : cleanedValue;
};

const formAnswerUpdatedHandler = (data: FormAnswerUpdatedDto) => {
  // data has:
  // Guid workflowGuid, Guid formResponseGuid, Guid formElementGuid, Guid answerGuid, string formCode, string questionCode, string answerValue
  // Update shared answers for real-time template substitution
  const key = `${data.formCode}:${data.questionCode}`;
  sharedAnswers.value[key] = data.answerValue;

  // also update the answer in the current workflow state so that if user opens a form that uses this answer, they will see the updated value
  const formResponse = workflow.value?.formResponses?.find(
    (fr) => fr.guid === data.formResponseGuid,
  );
  if (!formResponse) {
    return;
  }

  const element = formResponse.formDefinition?.components?.find(
    (fe) => fe.guid === data.formElementGuid,
  );
  if (!element) {
    return;
  }
  if (!formResponse.answers) {
    formResponse.answers = [];
  }
  const answer = formResponse.answers.find((a) => a.guid === data.answerGuid);
  if (answer) {
    // answer exists, update it.
    // The server sends a "cleaned" answerValue (TryDeserializeJsonString): JSON
    // scalar strings arrive unquoted, but list/array answers arrive as raw JSON
    // (e.g. ["CodeA","CodeB"]). Locally, answers must stay JSON-stringified
    // (they are JSON.parse'd on form open and by handleFormCodeTemplate), so we
    // re-encode only the scalars. Blindly JSON.stringify-ing here double-encoded
    // array answers, which broke showLabelsFromList in side-by-side view
    // (the codes no longer matched list items, so it fell back to raw values).
    answer.answerValue = reEncodeSharedAnswerValue(
      data.answerValue,
      answer.answerValue,
    );
  }
};

const workflowStatusChangedHandler = async (obj: any) => {
  console.log("Workflow status changed:", obj);

  // Close the drawer if the new status is in the configured close drawer statuses
  if (
    workflow.value?.workflowDefinition?.closeDrawerOnStatuses?.includes(
      obj.status,
    )
  ) {
    emit("closeDrawer");
  }

  // If a follow-up workflow was created, notify parent to switch to it
  if (obj.followUpWorkflowGuid) {
    emit("openFollowUpWorkflow", obj.followUpWorkflowGuid);
  }
  // Only refresh if this is the workflow we're currently viewing
  else if (obj.workflowGuid === props.complianceWorkflow.guid) {
    const msg = "Workflow status changed. Reloading...";
    ElMessage.info({
      message: msg,
      duration: getMessageTime(msg),
      showClose: true,
    });

    // Refresh the workflow to reflect form visibility changes
    await showWorkflow(props.complianceWorkflow);
  }
};

const joinHub = async () => {
  const guid = props.complianceWorkflow.guid;

  // join hub to listen for workflow update messages (Msg, FormAnswerUpdated, StatusChanged)
  // NOTE: PdfMsg subscription is ensured (and awaited) inside pdfLoadingStore.makePdf()
  // before the iframe navigation that triggers server-side PDF messages.
  workFlowHub = await hubManager.getHub(
    "workflowInstance",
    "/workflowInstance",
  );
  if (workFlowHub) {
    workFlowHub.off("Msg", workflowMsgHandler);
    workFlowHub.off("FormAnswerUpdated", formAnswerUpdatedHandler);
    workFlowHub.off("StatusChanged", workflowStatusChangedHandler);

    workFlowHub.on("Msg", workflowMsgHandler);
    workFlowHub.on("FormAnswerUpdated", formAnswerUpdatedHandler);
    workFlowHub.on("StatusChanged", workflowStatusChangedHandler);

    try {
      await workFlowHub.invoke(
        "JoinWorkflowGroup",
        guid,
        myInfoStore.userInfo.loginName,
      );
    } catch (error) {
      showError("Error joining workflow group:", error);
    }
  }
};

const leaveHub = async () => {
  const guid = props.complianceWorkflow.guid;

  if (guid && workFlowHub && workFlowHub.state === "Connected") {
    try {
      await workFlowHub.invoke("LeaveWorkflowGroup", guid);
    } catch (error) {
      showError("Error leaving workflow group:", error);
    }
  }

  // Remove only *our* listeners using the stable refs. Do not use bare .off("Event").
  // Never .stop() the shared "workflowInstance" connection.
  if (workFlowHub) {
    workFlowHub.off("Msg", workflowMsgHandler);
    workFlowHub.off("FormAnswerUpdated", formAnswerUpdatedHandler);
    workFlowHub.off("StatusChanged", workflowStatusChangedHandler);
  }

  // Do not null/stop the shared connection object; hubManager keeps it for other consumers.
};

let workFlowHub: HubConnection | null = null;

const workflowState = useWorkflow({
  updateStatus: async (status: string) => {
    if (!workflow.value?.guid) {
      showError("Workflow instance not found");
      return;
    }
    if (workflow.value.status === status) {
      return;
    }
    workflow.value.status = status;
    try {
      await updateWorkflow();
      // Refresh workflow data to apply server-side filtering rules
      // Only refresh if the update was successful
      await showWorkflow(props.complianceWorkflow);
    } catch (error) {
      // Error handling is already done in updateWorkflow
      // Don't refresh if update failed
      showError("Failed to update workflow status", error);
    }
  },
  updateStatusFromNameStamp: async (status: string) => {
    if (!workflow.value?.guid) {
      showError("Workflow instance not found");
      return;
    }
    if (workflow.value.status === status) {
      console.log("No need to update status");
      return;
    }
    workflow.value.status = status;
    // Bypass permission check for NameStamp status updates - anyone should be able to change workflow status via NameStamp
    await apiClient.workflowInstance
      .updateWorkflow(workflow.value.guid, workflow.value)
      .then(async (res) => {
        ElMessage.success("Workflow status updated");
        emit("updateWorkflow", res);
        // Refresh workflow data to apply server-side filtering rules
        // Only refresh if the update was successful
        await showWorkflow(props.complianceWorkflow);
      })
      .catch(showError);
  },
  getWorkflowStatus: () => workflow.value?.status ?? "",
  getWorkflow: () => workflow.value,
  deleteWorkflow: async () => {
    if (!workflow.value?.guid) {
      showError("Workflow instance not found");
      return;
    }
    await apiClient.workflowInstance
      .deleteWorkflowInstance(workflow.value.guid)
      .then(() => {
        ElMessage.success("Workflow deleted");
        emit("refreshList");
        emit("closeDrawer");
      })
      .catch(showError);
  },
});

const handleCloseFormDrawer = async () => {
  formDrawerOpen.value = false;
  workflowFormBeingViewed.value = undefined;
};

const addClassToBody = (className: string) => {
  document.body.classList.add(className);
};

const removeClassFromBody = (className: string) => {
  document.body.classList.remove(className);
};

// provide form state to child components
provide(workflowInjectKey, workflowState);

onMounted(async () => {
  if (props.fullScreen) {
    addClassToBody("fullScreen");
  }
  await showWorkflow(props.complianceWorkflow);

  // set workflow GUID so it can be accessed by the action button component to generate PDFs
  if (workflow.value?.guid) {
    pdfLoadingStore.setWorkflowGuid(workflow.value.guid);
  } else {
    pdfLoadingStore.reset();
  }

  statusOptions.value =
    workflow.value?.workflowDefinition?.statusList
      ?.map((s) => ({
        code: s.code,
        label: s.label,
      }))
      .filter(
        (status): status is { code: string; label: string } =>
          status.code !== null && status.label !== null,
      ) ?? [];

  // Determine which steps should be open using the centralized logic
  const stepsToOpen = determineStepsToOpen(workflow.value);
  // Include form GUIDs for compare mode (collapse items keyed by guid)
  const stepForms = workflowStepForms.value;
  const guidSteps = stepsToOpen
    .map((idx) => stepForms[idx - 1]?.guid)
    .filter((g): g is string => !!g);
  activeSteps.value = [...stepsToOpen, ...guidSteps];

  // how many forms are in the workflow - if only 1, open it by default
  if (workflowStepForms.value.length < 2) {
    viewMode.value = "stacked";
  }

  await leaveHub(); // in development, onMounted can be called multiple times, so leave first
  await joinHub();

  // Initialize time frame date override
  initializeDateOverride();

  watchForNoClickLinks();

  emit("loaded");

  // Handle focusFormResponseGuid prop
  if (props.focusFormResponseGuid) {
    const matching = workflow.value?.formResponses?.find(
      (fr) => fr.guid === props.focusFormResponseGuid,
    );

    if (matching && matching.guid) {
      // Always switch to stacked mode and expand the focused form but do NOT collapse others.
      viewMode.value = "stacked";
      // Ensure the focused form is included in the activeSteps (expand it if necessary)
      if (!activeSteps.value.includes(matching.guid)) {
        activeSteps.value = [...activeSteps.value, matching.guid];
      }
      // Also select it for compare UI state so the sidebar highlights it
      selectedFormGuids.value = [matching.guid];

      // Scroll the focused form into view
      await scrollToForm(matching.guid);
    }
  }
});

let noClickObserver: MutationObserver | null = null;

const watchForNoClickLinks = () => {
  // Clean up existing observer if any
  if (noClickObserver) {
    noClickObserver.disconnect();
  }

  // Set up MutationObserver to watch for added elements with noClick class
  noClickObserver = new MutationObserver((mutations) => {
    let needsDisabling = false;

    mutations.forEach((mutation) => {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        // Check if any added nodes are or contain noClick links
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            // Check for noClick elements directly added
            if (
              element.classList?.contains("noClick") ||
              element.querySelectorAll?.("a.noClick, .noClick a").length > 0
            ) {
              needsDisabling = true;
            }
          }
        });
      }
    });

    if (needsDisabling) {
      // Use requestAnimationFrame to batch DOM updates
      requestAnimationFrame(() => {
        disableNoClickLinks();
      });
    }
  });

  // Start observing the document with the configured parameters
  noClickObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

// Track which links have already been disabled to avoid duplicate event listeners
const disabledLinks = new WeakSet<Element>();

const disableNoClickLinks = () => {
  const noClickLinks = document.querySelectorAll("a.noClick, .noClick a");

  noClickLinks.forEach((link) => {
    // Skip if already disabled
    if (disabledLinks.has(link)) {
      return;
    }

    link.addEventListener("click", (event) => {
      event.preventDefault(); // Stops the default navigation behavior
    });
    link.setAttribute("aria-disabled", "true");

    // Mark as disabled
    disabledLinks.add(link);
  });
};

onUnmounted(() => {
  leaveHub();

  // Clean up MutationObserver
  if (noClickObserver) {
    noClickObserver.disconnect();
    noClickObserver = null;
  }

  if (pdfLoadingStore.elMessageHandler) {
    pdfLoadingStore.elMessageHandler.close();
    pdfLoadingStore.setElMessageHandler(null);
  }
  pdfLoadingStore.reset();

  if (props.fullScreen) {
    removeClassFromBody("fullScreen");
  }
});

const localDrawerWidth = computed(() => {
  return props.fullScreen ? "100%" : drawerWidth;
});

const getWorkflowStatusLabel = (statusCode: string) => {
  return (
    statusOptions.value?.find((status) => status.code === statusCode)?.label ??
    statusCode
  );
};

/**
 * Get the substituted title for a form step by applying template variable substitution
 * to the form definition name.
 */
const getSubstitutedFormTitle = (step: FormResponse): string => {
  if (!step.formDefinition?.name) {
    return step.formDefinition?.name ?? "Unknown Form";
  }

  // Only process titles that contain template variables
  if (!step.formDefinition.name.includes("{{")) {
    return step.formDefinition.name;
  }

  try {
    // Use the useFormResponse composable to access template substitution
    const formResponseState = useFormResponse({
      formDefinition: step.formDefinition,
      formResponse: step,
      displayMode: FormDisplayMode.READ_ONLY,
      workflow: workflow.value,
      sharedAnswers: sharedAnswers.value,
      onWorkflowRefresh: async () => {
        // Refresh workflow data when status changes in child components
        if (props.complianceWorkflow) {
          await showWorkflow(props.complianceWorkflow);
        }
      },
    });

    // Apply template substitution to the form name
    const substitutedTitle = formResponseState.substituteTemplatedText(
      step.formDefinition.name,
    );

    return substitutedTitle ?? step.formDefinition.name;
  } catch (error) {
    console.warn("Error substituting form title:", error);
    return step.formDefinition.name;
  }
};

/**
 * Computed property that returns substituted titles for all workflow step forms.
 * This prevents recalculating on every render and improves performance.
 */
const substitutedFormTitles = computed(() => {
  const titlesMap = new Map<string, string>();

  workflowStepForms.value.forEach((step) => {
    if (step.guid) {
      titlesMap.set(step.guid, getSubstitutedFormTitle(step));
    }
  });

  return titlesMap;
});

/**
 * Get substituted title for a specific form step by GUID.
 */
const getFormTitleByGuid = (step: FormResponse): string => {
  if (!step.guid) {
    return step.formDefinition?.name ?? "Unknown Form";
  }
  return (
    substitutedFormTitles.value.get(step.guid) ??
    step.formDefinition?.name ??
    "Unknown Form"
  );
};

/**
 * Splits camelCase/PascalCase text by capitals for display purposes.
 */
const splitByCaps = (text: string): string => {
  return text
    .replaceAll(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Gets the display name for a form response status.
 * Uses the defined display names, or falls back to SplitByCaps formatting.
 */
const getFormStatusLabel = (status: FormResponseStatus): string => {
  return (
    formResponseStatusDisplayNames[status] ?? splitByCaps(status ?? "Unknown")
  );
};

/**
 * Checks if a form should show its status in the workflow view.
 */
const shouldShowFormStatus = (step: FormResponse): boolean => {
  return step.formDefinition?.metaInfo?.showStatusInWorkflowView ?? false;
};

// Compare mode form selection methods
const toggleFormSelection = (formGuid: string) => {
  const index = selectedFormGuids.value.indexOf(formGuid);

  if (index > -1) {
    selectedFormGuids.value.splice(index, 1);
    return;
  }

  // Add — cap at MAX_COMPARE_FORMS by evicting the oldest
  if (selectedFormGuids.value.length >= MAX_COMPARE_FORMS) {
    selectedFormGuids.value.shift();
  }
  selectedFormGuids.value.push(formGuid);
};

const autoSelectDefaultForms = () => {
  selectedFormGuids.value = allComparableForms.value
    .slice(0, MAX_COMPARE_FORMS)
    .map((f) => f.guid || "")
    .filter(Boolean);
};

/**
 * Handle a sidebar item click.
 *
 * Stacked mode: open ONLY the clicked form (close all others).
 * Compare mode: toggle the form in the selection (max 2).
 */
const handleSidebarItemClick = (formGuid?: string) => {
  if (!formGuid) {
    return;
  }

  if (viewMode.value === "compare") {
    toggleFormSelection(formGuid);
    // Ensure the form's collapse panel is expanded
    if (!activeSteps.value.includes(formGuid)) {
      activeSteps.value = [...activeSteps.value, formGuid];
    }
  } else {
    // Stacked: open only this form, close all others
    activeSteps.value = [formGuid];
  }
};

const toggleSidebar = () => {
  compareModeSidebarCollapsed.value = !compareModeSidebarCollapsed.value;
};

const makePdf = async () => {
  const pdfCode = selectedPDF.value;
  if (workflow.value?.guid) {
    // Make sure the "self" hub is in the picture (the viewer also joins
    // "workflowInstance", which populates the *global* hubManager.ConnectionId).
    // We explicitly ask for the ID belonging to "self" (tracked per-hub in
    // hubManager) because that's the connection on which the PdfMsg listener
    // for progress (step 2 etc.) is attached. The store will also ensure the
    // listener, but passing the correct ID here is what the backend uses to
    // target the message.
    await hubManager.getHub("self", "/self");
    const selfConnId =
      hubManager.getHubConnectionId("self") ?? hubManager.ConnectionId ?? "";
    await pdfLoadingStore.makePdf(pdfCode, workflow.value.guid, selfConnId);
  }
};

/**
 * Build the collapse :name for a form in the unified layout.
 * We always use the GUID so the sidebar click (which sets GUIDs) works correctly.
 */
const getCollapseName = (form: FormResponse): string => {
  return form.guid ?? "";
};

/** Scroll the collapse item for a given form GUID into view and focus it.
 * This expands the item if it's not already expanded but does not close others.
 */
const scrollToForm = async (formGuid: string) => {
  if (!formGuid) {
    return;
  }
  await nextTick();

  setTimeout(() => {
    const el = document.getElementById(`form-${formGuid}`);
    if (!el) {
      return;
    }

    const scrollParent =
      el.closest(".el-drawer__body") ?? el.closest(".workflow-inline");
    if (scrollParent) {
      scrollParent.scrollTop =
        el.offsetTop - scrollParent.getBoundingClientRect().top;
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, 300);
};

// --- Change form-response version (admin) ---

/**
 * Per-form version metadata for the whole workflow, computed once. Reused by upgradeOptionsFor,
 * which the template calls repeatedly per render (v-if + v-for) — avoids re-grouping/sorting each
 * call.
 */
const versionInfoByGuid = computed(() =>
  buildVersionInfo(workflow.value?.workflowDefinition?.formDefinitions ?? []),
);

/**
 * The NEWER versions this response can be upgraded TO. Upgrading creates a new response on the
 * chosen version (copying answers forward by QuestionCode) and keeps the current response as the
 * read-only original. Empty when there is no newer version, or upgrades are disabled.
 */
const upgradeOptionsFor = (form: FormResponse) => {
  const defs = workflow.value?.workflowDefinition?.formDefinitions ?? [];
  const currentDef = defs.find((fd) => fd.guid === form.formDefinitionGuid);
  if (!currentDef) return [];
  // The designer can lock a version against upgrades (Form Properties tab). If this response's
  // current version is locked, offer no upgrade.
  if (currentDef.metaInfo?.disableVersionUpgrade) return [];
  // A superseded response is already an "original" — do not offer to upgrade it again.
  if (isSupersededResponse(form)) return [];
  const info = versionInfoByGuid.value;
  const currentVersion = info.get(currentDef.guid)?.versionNumber ?? 0;
  // Only offer versions NEWER than the current one, and not themselves locked.
  return siblingVersionsOf(currentDef, defs)
    .filter((fd) => {
      if (fd.guid === form.formDefinitionGuid) return false;
      if (fd.metaInfo?.disableVersionUpgrade) return false;
      return (info.get(fd.guid)?.versionNumber ?? 0) > currentVersion;
    })
    .map((fd) => ({
      guid: fd.guid,
      label: `v${info.get(fd.guid)?.versionNumber ?? "?"} — ${fd.name}`,
    }));
};

const upgradingVersion = ref<string | null>(null);

const handleUpgradeVersion = async (
  form: FormResponse,
  targetFormDefinitionGuid: string,
) => {
  if (
    !workflow.value?.guid ||
    !form.guid ||
    targetFormDefinitionGuid === form.formDefinitionGuid
  ) {
    return;
  }
  // Defensive — the dropdown already hides invalid/locked targets.
  if (
    !upgradeOptionsFor(form).some((o) => o.guid === targetFormDefinitionGuid)
  ) {
    return;
  }
  try {
    await ElMessageBox.confirm(
      "Upgrade this form to the selected version? A new form will be created with this version " +
        "and the current answers copied over. The current version is kept as a read-only " +
        "original you can still view under 'previous versions'.",
      "Upgrade Version",
      {
        confirmButtonText: "Upgrade",
        cancelButtonText: "Cancel",
        type: "warning",
      },
    );
  } catch {
    return;
  }
  upgradingVersion.value = form.guid;
  try {
    const newResponse =
      await apiClient.workflowInstance.upgradeFormResponseVersion(
        workflow.value.guid,
        form.guid,
        targetFormDefinitionGuid,
      );
    ElMessage.success("Form upgraded to the new version");
    await showWorkflow(props.complianceWorkflow);
    // Expand the newly-created response so the user lands on it.
    if (newResponse?.guid) {
      await nextTick();
      if (!activeSteps.value.includes(newResponse.guid)) {
        activeSteps.value = [...activeSteps.value, newResponse.guid];
      }
    }
  } catch (e) {
    showError(e);
  } finally {
    upgradingVersion.value = null;
  }
};
</script>

<template>
  <!-- Inline Mode: renders workflow content directly without a drawer -->
  <div v-if="props.inline && workflow?.guid" class="workflow-inline">
    <div class="inline-header">
      <div class="inline-header-left">
        <span class="inline-header-title" :title="workflow.guid">
          {{ workflow.name }}
        </span>
        <el-button
          v-if="canEditWorkflowName && !isEffectivelyReadOnly"
          :icon="Edit"
          text
          size="small"
          @click="editing = true"
        />
        <el-tag v-if="!shouldHideWorkflowStatus" size="small">
          {{ getWorkflowStatusLabel(workflow.status ?? "") }}
        </el-tag>
      </div>
      <div class="inline-header-actions">
        <div
          v-if="workflow.entityGuid && showEntityName"
          class="entity-info"
          @click="entityDrawerOpen = true"
        >
          <i :class="'mdi mdi-' + workflow.entity?.entityDefinition?.icon" />
          {{ workflow.entity?.name }}
        </div>
        <div
          v-if="hasSetupBasicRole"
          title="Developer Overrides"
          class="devOverrides"
        >
          <div>
            <el-button
              size="small"
              @click="editingWorkflowStatus = !editingWorkflowStatus"
            >
              Change Status
            </el-button>
            <el-select
              v-if="
                editingWorkflowStatus &&
                !isEffectivelyReadOnly &&
                workflow.status
              "
              v-model="workflow.status"
              placeholder="Select a status"
              style="width: 150px"
              filterable
              size="small"
              @change="
                () => {
                  updateWorkflow();
                  editingWorkflowStatus = false;
                }
              "
            >
              <el-option
                v-for="status in statusOptions"
                :key="status.label"
                :label="status.label"
                :value="status.code"
              />
            </el-select>
          </div>
          <div>
            <el-button v-if="workflow?.guid" size="small" @click="makePdf()"
              >Test PDF</el-button
            >
            <el-select
              v-if="pdfCodes.length > 0"
              v-model="selectedPDF"
              placeholder="PDF Code"
              style="width: 100px"
              size="small"
            >
              <el-option
                v-for="pdf in pdfCodes"
                :key="pdf.value"
                :label="pdf.label"
                :value="pdf.value"
              />
            </el-select>
          </div>
          <div v-if="isDevEnvironment && hasTimeFrames">
            <div style="display: flex; flex-direction: column; gap: 5px">
              <div v-if="timeFrameOverrideDate" style="font-size: 0.85rem">
                Today is:
                <span style="color: var(--el-color-danger)">{{
                  new Date(timeFrameOverrideDate).toDateString()
                }}</span>
                <el-button
                  v-if="timeFrameOverrideDate"
                  size="small"
                  @click="clearDateOverride"
                >
                  Reset
                </el-button>
              </div>
              <div v-else style="display: flex; gap: 5px; align-items: center">
                <el-date-picker
                  v-model="timeFrameOverrideDate"
                  type="date"
                  placeholder="Override date"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  size="small"
                  style="width: 200px"
                />
              </div>
            </div>
          </div>
        </div>
        <el-button
          v-if="!props.fullScreen && isCancelable"
          type="danger"
          plain
          size="small"
          @click="handleCancelWorkflow"
        >
          Cancel
        </el-button>
      </div>
    </div>

    <template v-if="workflow">
      <!-- View Mode Selector -->
      <div v-if="!isMobile && shouldShowCompareMode" class="view-mode-selector">
        <el-radio-group v-model="viewMode" size="small">
          <el-radio-button label="compare">Side-by-side</el-radio-button>
          <el-radio-button label="stacked">Vertical</el-radio-button>
        </el-radio-group>
      </div>

      <!--
        ── Unified layout: sidebar is ALWAYS visible when there are forms ──
        Both stacked and compare modes use this container. The sidebar shows
        ALL forms in the workflow at all times.
      -->
      <div
        class="workflow-layout-container"
        :class="{
          'sidebar-collapsed': compareModeSidebarCollapsed,
          'no-sidebar': !shouldShowCompareMode,
        }"
      >
        <!-- Sidebar: only render when compare mode is available -->
        <div
          v-if="shouldShowCompareMode"
          class="workflow-sidebar"
          :class="{ collapsed: compareModeSidebarCollapsed }"
        >
          <div class="sidebar-header">
            <span v-if="!compareModeSidebarCollapsed" class="sidebar-title">
              Forms
            </span>
            <el-button
              size="small"
              text
              :title="
                compareModeSidebarCollapsed
                  ? 'Show forms sidebar'
                  : 'Hide sidebar'
              "
              @click="toggleSidebar"
            >
              <div class="sidebar-arrow-box">
                <el-icon>
                  <ArrowRightBold v-if="compareModeSidebarCollapsed" />
                  <ArrowLeftBold v-else />
                </el-icon>
              </div>
            </el-button>
            <span
              v-if="compareModeSidebarCollapsed"
              class="sidebar-title-vertical"
              @click="toggleSidebar"
            >
              Forms
            </span>
          </div>

          <div v-if="!compareModeSidebarCollapsed" class="sidebar-content">
            <div
              v-for="form in allComparableForms"
              :key="form.guid"
              class="sidebar-form-item"
              :class="{
                'is-active':
                  viewMode === 'stacked'
                    ? activeSteps.includes(form.guid ?? '')
                    : selectedFormGuids.includes(form.guid ?? ''),
              }"
              @click="handleSidebarItemClick(form.guid)"
            >
              <div class="form-item-header">
                <!-- In compare mode show a checkbox to indicate selection -->
                <el-checkbox
                  v-if="viewMode === 'compare'"
                  :model-value="selectedFormGuids.includes(form.guid || '')"
                  :label="
                    getEntityFormConfig(form)?.label ??
                    getEntityFormConfig(form)?.entityDefinition?.name ??
                    getFormTitleByGuid(form)
                  "
                  @click.stop="toggleFormSelection(form.guid || '')"
                />
                <span v-else class="form-item-title">{{
                  getEntityFormConfig(form)?.label ??
                  getEntityFormConfig(form)?.entityDefinition?.name ??
                  getFormTitleByGuid(form)
                }}</span>
              </div>
            </div>

            <!-- User-created forms: show "Create" button in sidebar -->
            <div
              v-for="item in creatableForms"
              :key="'create-' + item.formDefinition.guid"
              class="sidebar-form-item sidebar-create-item"
            >
              <el-button
                size="small"
                :disabled="!item.canCreate || isEffectivelyReadOnly"
                :loading="creatingForm === item.formDefinition.guid"
                style="width: 100%"
                @click.stop="handleCreateFormInWorkflow(item.formDefinition)"
              >
                Create {{ item.formDefinition.name }}
              </el-button>
            </div>

            <template v-if="$slots['sidebar-footer']">
              <div class="sidebar-separator" />
              <slot name="sidebar-footer" />
            </template>
          </div>
        </div>

        <!-- Main area: Selected forms in grid -->
        <div class="workflow-main-area">
          <div v-if="supersededResponseCount > 0" class="previous-versions-bar">
            <el-checkbox v-model="showPreviousVersions" size="small">
              Show previous versions ({{ supersededResponseCount }})
            </el-checkbox>
          </div>
          <el-collapse
            v-model="activeSteps"
            :class="[
              'workflow-forms-collapse',
              viewMode === 'compare' ? 'compare-mode' : 'stacked-mode',
              { 'single-form': isSingleForm },
            ]"
          >
            <template
              v-for="form in viewMode === 'compare'
                ? selectedForms
                : allComparableForms"
              :key="form.guid"
            >
              <el-collapse-item
                :id="`form-${form.guid}`"
                :name="getCollapseName(form)"
              >
                <template #title>
                  <div class="form-title-container">
                    <span class="form-title">{{
                      getEntityFormConfig(form)?.label ??
                      getEntityFormConfig(form)?.entityDefinition?.name ??
                      getFormTitleByGuid(form)
                    }}</span>
                    <span v-if="shouldShowFormStatus(form)" class="form-status">
                      {{ getFormStatusLabel(form.status) }}
                    </span>
                    <el-tag
                      v-if="isSupersededResponse(form)"
                      type="info"
                      size="small"
                      title="A previous version, kept read-only with its original captured answers"
                    >
                      Previous version
                    </el-tag>
                    <el-select
                      v-if="
                        hasSetupBasicRole && upgradeOptionsFor(form).length > 0
                      "
                      :model-value="undefined"
                      placeholder="Upgrade to…"
                      size="small"
                      style="width: 200px; margin-left: auto"
                      :loading="upgradingVersion === form.guid"
                      title="Upgrade this form to a newer version"
                      @click.stop
                      @change="
                        (guid: string) => handleUpgradeVersion(form, guid)
                      "
                    >
                      <el-option
                        v-for="opt in upgradeOptionsFor(form)"
                        :key="opt.guid"
                        :label="opt.label"
                        :value="opt.guid"
                      />
                    </el-select>
                  </div>
                </template>

                <!-- Entity form extra actions -->
                <template v-if="form.formType === FormType.ENTITY_DEFINITION">
                  <div class="entity-form-actions">
                    <el-button
                      v-if="
                        !getEntityFormConfig(form)
                          ?.autoCreateEntityOnSubmission &&
                        form.status === FormResponseStatus.SUBMITTED
                      "
                      type="success"
                      size="small"
                      :loading="creatingEntityFromForm === form.guid"
                      @click="handleCreateEntityFromForm(form)"
                    >
                      Create Entity
                    </el-button>
                  </div>
                </template>

                <FormResponseTemplate
                  v-if="activeSteps.includes(getCollapseName(form))"
                  :key="`${form.guid}-${form.formDefinitionGuid}`"
                  class="form"
                  :form-guid="form.guid"
                  :response-guid="form.guid"
                  :response="form"
                  :workflow="workflow"
                  :is-previous-version="isSupersededResponse(form)"
                  :incoming-display-mode="getFormDisplayMode(form)"
                  :shared-answers="sharedAnswersForProps"
                  :on-workflow-refresh="
                    async () => {
                      if (props.complianceWorkflow) {
                        await showWorkflow(props.complianceWorkflow);
                      }
                    }
                  "
                />
              </el-collapse-item>
            </template>
          </el-collapse>

          <!-- General (WORKFLOW_DEFINITION_MAIN_FORM) forms table -->
          <el-collapse
            v-if="generalForms.length > 0 && viewMode !== 'compare'"
            v-model="activeSteps"
          >
            <el-collapse-item title="Forms">
              <el-table
                :data="generalForms"
                :default-sort="{ prop: 'createdAt', order: 'descending' }"
                style="width: 100%"
                max-height="250"
              >
                <el-table-column label="Name">
                  <template #default="{ row }: Slotted<FormResponseDto>">
                    <el-link @click="handleViewForm(row)">
                      {{ getFormName(row.formDefinitionGuid) }}
                    </el-link>
                  </template>
                </el-table-column>
                <el-table-column prop="formType" label="Type">
                  <template #default="{ row }: Slotted<FormDefinition>">
                    {{ formTypeDescriptions[row.formType].label }}
                  </template>
                </el-table-column>
                <el-table-column label="Created">
                  <template #default="scope">
                    <div class="overflow-text">{{ scope.row.createdAt }}</div>
                  </template>
                </el-table-column>
                <el-table-column label="Updated">
                  <template #default="scope">
                    <div class="overflow-text">{{ scope.row.updatedAt }}</div>
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>

      <!-- No-forms fallback (edge case) -->
      <template
        v-if="allComparableForms.length === 0 && generalForms.length > 0"
      >
        <el-collapse v-model="activeSteps">
          <el-collapse-item title="Forms">
            <el-table
              :data="generalForms"
              :default-sort="{ prop: 'createdAt', order: 'descending' }"
              style="width: 100%"
              max-height="250"
            >
              <el-table-column label="Name">
                <template #default="{ row }: Slotted<FormResponseDto>">
                  <el-link @click="handleViewForm(row)">
                    {{ getFormName(row.formDefinitionGuid) }}
                  </el-link>
                </template>
              </el-table-column>

              <el-table-column prop="formType" label="Type">
                <template #default="{ row }: Slotted<FormDefinition>">
                  {{ formTypeDescriptions[row.formType].label }}
                </template>
              </el-table-column>
              <el-table-column label="Created">
                <template #default="scope">
                  <div class="overflow-text">
                    {{ scope.row.createdAt }}
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="Updated">
                <template #default="scope">
                  <div class="overflow-text">
                    {{ scope.row.updatedAt }}
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </template>
    </template>
    <div v-else>
      <el-empty description="Loading..." />
    </div>
  </div>

  <!-- Drawer Mode: original drawer-based rendering -->
  <el-drawer
    v-if="!props.inline && workflow?.guid"
    :model-value="drawerOpen"
    :title="workflow.name || '\u00A0'"
    :destroy-on-close="true"
    direction="rtl"
    :size="localDrawerWidth"
    :show-close="false"
    append-to-body
    class="workflow-drawer"
    :before-close="handleClosing"
    @closed="handleClose"
  >
    <template #header>
      <div
        v-if="editing && canEditWorkflowName && !isEffectivelyReadOnly"
        class="el-drawer__title"
      >
        <h2>
          <el-input
            v-model="workflow.name"
            placeholder="Workflow Name"
            :style="{ width: '300px' }"
            @keyup.enter="updateWorkflow"
          />
        </h2>

        <el-button
          class="el-drawer__close-btn"
          :icon="CircleCheck"
          size="large"
          @click="updateWorkflow"
        ></el-button>
      </div>
      <div v-else class="el-drawer__title withEntity">
        <div class="workflow-info">
          <h2 :title="workflow.guid">{{ workflow.name }}</h2>
          <el-button
            v-if="canEditWorkflowName && !isEffectivelyReadOnly"
            class="el-drawer__close-btn"
            :icon="Edit"
            size="large"
            @click="editing = true"
          ></el-button>
          <el-tag v-if="!shouldHideWorkflowStatus" size="large">
            {{ getWorkflowStatusLabel(workflow.status ?? "") }}
          </el-tag>
        </div>
        <div
          v-if="hasSetupBasicRole && isDevEnvironment"
          title="Developer Overrides"
          class="devOverrides"
        >
          <div>
            <div>
              <el-button
                size="small"
                @click="editingWorkflowStatus = !editingWorkflowStatus"
              >
                Change Status
              </el-button>
            </div>
            <div>
              <el-select
                v-if="
                  editingWorkflowStatus &&
                  !isEffectivelyReadOnly &&
                  workflow.status
                "
                v-model="workflow.status"
                placeholder="Select a status"
                style="width: 150px"
                filterable
                @change="
                  () => {
                    updateWorkflow();
                    editingWorkflowStatus = false;
                  }
                "
              >
                <el-option
                  v-for="status in statusOptions"
                  :key="status.label"
                  :label="status.label"
                  :value="status.code"
                />
              </el-select>
            </div>
          </div>
          <div>
            <el-button v-if="workflow?.guid" size="small" @click="makePdf()"
              >Test PDF</el-button
            >
            <el-select
              v-if="pdfCodes.length > 0"
              v-model="selectedPDF"
              placeholder="PDF Code"
              style="width: 100px"
              size="small"
            >
              <el-option
                v-for="pdf in pdfCodes"
                :key="pdf.value"
                :label="pdf.label"
                :value="pdf.value"
              />
            </el-select>
          </div>

          <!-- Time Frame Date Override -->
          <div v-if="isDevEnvironment && hasTimeFrames">
            <div style="display: flex; flex-direction: column; gap: 5px">
              <div v-if="timeFrameOverrideDate" style="font-size: 1rem">
                Today is:
                <span style="color: var(--el-color-danger)">{{
                  new Date(timeFrameOverrideDate).toDateString()
                }}</span>
                <el-button
                  v-if="timeFrameOverrideDate"
                  size="small"
                  @click="clearDateOverride"
                  >Reset</el-button
                >
              </div>
              <div v-else style="display: flex; gap: 5px; align-items: center">
                <el-date-picker
                  v-model="timeFrameOverrideDate"
                  type="date"
                  placeholder="Override date"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  size="small"
                  style="width: 200px"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="workflow.entityGuid && showEntityName"
          class="entity-info"
          @click="entityDrawerOpen = true"
        >
          <i :class="'mdi mdi-' + workflow.entity?.entityDefinition?.icon" />
          {{ workflow.entity?.name }}
        </div>
        <div>
          <el-button
            v-if="workflow.workflowDefinition?.enableDownloading"
            :icon="Download"
            @click="makePdf"
            >Download All</el-button
          >
          <el-button
            v-if="!props.fullScreen && isCancelable"
            type="danger"
            plain
            class="drawer-close-btn"
            @click="handleCancelWorkflow"
            >Cancel</el-button
          >
          <el-button
            v-else-if="!props.fullScreen"
            class="drawer-close-btn"
            @click="drawerOpen = false"
            >Close</el-button
          >
        </div>
      </div>
    </template>
    <template v-if="workflow">
      <!-- View Mode Selector -->
      <div v-if="!isMobile && shouldShowCompareMode" class="view-mode-selector">
        <el-radio-group v-model="viewMode" size="small">
          <el-radio-button label="compare">Side-by-side</el-radio-button>
          <el-radio-button label="stacked">Vertical</el-radio-button>
        </el-radio-group>
      </div>
    </template>

    <template v-if="workflow">
      <!--
        ── Unified layout (drawer): sidebar always visible ──
      -->
      <div
        class="workflow-layout-container"
        :class="{
          'sidebar-collapsed': compareModeSidebarCollapsed,
          'no-sidebar': !shouldShowCompareMode,
        }"
      >
        <!-- Sidebar -->
        <div
          v-if="shouldShowCompareMode"
          class="workflow-sidebar"
          :class="{ collapsed: compareModeSidebarCollapsed }"
        >
          <div class="sidebar-header">
            <span v-if="!compareModeSidebarCollapsed" class="sidebar-title">
              Forms
            </span>
            <el-button
              size="small"
              text
              :title="
                compareModeSidebarCollapsed
                  ? 'Show forms sidebar'
                  : 'Hide sidebar'
              "
              @click="toggleSidebar"
            >
              <div class="sidebar-arrow-box">
                <el-icon>
                  <ArrowRightBold v-if="compareModeSidebarCollapsed" />
                  <ArrowLeftBold v-else />
                </el-icon>
              </div>
            </el-button>
            <span
              v-if="compareModeSidebarCollapsed"
              class="sidebar-title-vertical"
              @click="toggleSidebar"
            >
              Forms
            </span>
          </div>

          <div v-if="!compareModeSidebarCollapsed" class="sidebar-content">
            <div
              v-for="form in allComparableForms"
              :key="form.guid"
              class="sidebar-form-item"
              :class="{
                'is-active':
                  viewMode === 'stacked'
                    ? activeSteps.includes(form.guid ?? '')
                    : selectedFormGuids.includes(form.guid ?? ''),
              }"
              @click="handleSidebarItemClick(form.guid)"
            >
              <div class="form-item-header">
                <el-checkbox
                  v-if="viewMode === 'compare'"
                  :model-value="selectedFormGuids.includes(form.guid || '')"
                  :label="
                    getEntityFormConfig(form)?.label ??
                    getEntityFormConfig(form)?.entityDefinition?.name ??
                    getFormTitleByGuid(form)
                  "
                  @click.stop="toggleFormSelection(form.guid || '')"
                />
                <span v-else class="form-item-title">{{
                  getEntityFormConfig(form)?.label ??
                  getEntityFormConfig(form)?.entityDefinition?.name ??
                  getFormTitleByGuid(form)
                }}</span>
              </div>
            </div>

            <!-- User-created forms: show "Create" button in sidebar -->
            <div
              v-for="item in creatableForms"
              :key="'create-' + item.formDefinition.guid"
              class="sidebar-form-item sidebar-create-item"
            >
              <el-button
                size="small"
                :disabled="!item.canCreate || isEffectivelyReadOnly"
                :loading="creatingForm === item.formDefinition.guid"
                style="width: 100%"
                @click.stop="handleCreateFormInWorkflow(item.formDefinition)"
              >
                Create {{ item.formDefinition.name }}
              </el-button>
            </div>

            <template v-if="$slots['sidebar-footer']">
              <div class="sidebar-separator" />
              <slot name="sidebar-footer" />
            </template>
          </div>
        </div>

        <!-- Main content area -->
        <div class="workflow-main-area">
          <div v-if="supersededResponseCount > 0" class="previous-versions-bar">
            <el-checkbox v-model="showPreviousVersions" size="small">
              Show previous versions ({{ supersededResponseCount }})
            </el-checkbox>
          </div>
          <el-collapse
            v-model="activeSteps"
            :class="[
              'workflow-forms-collapse',
              viewMode === 'compare' ? 'compare-mode' : 'stacked-mode',
              { 'single-form': isSingleForm },
            ]"
          >
            <template
              v-for="form in viewMode === 'compare'
                ? selectedForms
                : allComparableForms"
              :key="form.guid"
            >
              <el-collapse-item
                :id="`form-${form.guid}`"
                :name="getCollapseName(form)"
              >
                <template #title>
                  <div class="form-title-container">
                    <span class="form-title">{{
                      getEntityFormConfig(form)?.label ??
                      getEntityFormConfig(form)?.entityDefinition?.name ??
                      getFormTitleByGuid(form)
                    }}</span>
                    <span v-if="shouldShowFormStatus(form)" class="form-status">
                      {{ getFormStatusLabel(form.status) }}
                    </span>
                    <el-tag
                      v-if="isSupersededResponse(form)"
                      type="info"
                      size="small"
                      title="A previous version, kept read-only with its original captured answers"
                    >
                      Previous version
                    </el-tag>
                    <el-select
                      v-if="
                        hasSetupBasicRole && upgradeOptionsFor(form).length > 0
                      "
                      :model-value="undefined"
                      placeholder="Upgrade to…"
                      size="small"
                      style="width: 200px; margin-left: auto"
                      :loading="upgradingVersion === form.guid"
                      title="Upgrade this form to a newer version"
                      @click.stop
                      @change="
                        (guid: string) => handleUpgradeVersion(form, guid)
                      "
                    >
                      <el-option
                        v-for="opt in upgradeOptionsFor(form)"
                        :key="opt.guid"
                        :label="opt.label"
                        :value="opt.guid"
                      />
                    </el-select>
                  </div>
                </template>

                <template v-if="form.formType === FormType.ENTITY_DEFINITION">
                  <div class="entity-form-actions">
                    <el-button
                      v-if="
                        !getEntityFormConfig(form)
                          ?.autoCreateEntityOnSubmission &&
                        form.status === FormResponseStatus.SUBMITTED
                      "
                      type="success"
                      size="small"
                      :loading="creatingEntityFromForm === form.guid"
                      @click="handleCreateEntityFromForm(form)"
                    >
                      Create Entity
                    </el-button>
                  </div>
                </template>

                <FormResponseTemplate
                  v-if="activeSteps.includes(getCollapseName(form))"
                  :key="`${form.guid}-${form.formDefinitionGuid}`"
                  class="form"
                  :form-guid="form.guid"
                  :response-guid="form.guid"
                  :response="form"
                  :workflow="workflow"
                  :is-previous-version="isSupersededResponse(form)"
                  :incoming-display-mode="getFormDisplayMode(form)"
                  :shared-answers="sharedAnswersForProps"
                  :on-workflow-refresh="
                    async () => {
                      if (props.complianceWorkflow) {
                        await showWorkflow(props.complianceWorkflow);
                      }
                    }
                  "
                />
              </el-collapse-item>
            </template>
          </el-collapse>

          <!-- General forms table -->
          <el-collapse
            v-if="generalForms.length > 0 && viewMode !== 'compare'"
            v-model="activeSteps"
          >
            <el-collapse-item title="Forms">
              <el-table
                :data="generalForms"
                :default-sort="{ prop: 'createdAt', order: 'descending' }"
                style="width: 100%"
                max-height="250"
              >
                <el-table-column label="Name">
                  <template #default="{ row }: Slotted<FormResponseDto>">
                    <el-link @click="handleViewForm(row)">
                      {{ getFormName(row.formDefinitionGuid) }}
                    </el-link>
                  </template>
                </el-table-column>
                <el-table-column prop="formType" label="Type">
                  <template #default="{ row }: Slotted<FormDefinition>">
                    {{ formTypeDescriptions[row.formType].label }}
                  </template>
                </el-table-column>
                <el-table-column label="Created">
                  <template #default="scope">
                    <div class="overflow-text">{{ scope.row.createdAt }}</div>
                  </template>
                </el-table-column>
                <el-table-column label="Updated">
                  <template #default="scope">
                    <div class="overflow-text">{{ scope.row.updatedAt }}</div>
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>

      <!-- No-forms fallback -->
      <template
        v-if="allComparableForms.length === 0 && generalForms.length > 0"
      >
        <el-collapse v-model="activeSteps">
          <el-collapse-item title="Forms">
            <el-table
              :data="generalForms"
              :default-sort="{ prop: 'createdAt', order: 'descending' }"
              style="width: 100%"
              max-height="250"
            >
              <el-table-column label="Name">
                <template #default="{ row }: Slotted<FormResponseDto>">
                  <el-link @click="handleViewForm(row)">
                    {{ getFormName(row.formDefinitionGuid) }}
                  </el-link>
                </template>
              </el-table-column>
              <el-table-column prop="formType" label="Type">
                <template #default="{ row }: Slotted<FormDefinition>">
                  {{ formTypeDescriptions[row.formType].label }}
                </template>
              </el-table-column>
              <el-table-column label="Created">
                <template #default="scope">
                  <div class="overflow-text">{{ scope.row.createdAt }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Updated">
                <template #default="scope">
                  <div class="overflow-text">{{ scope.row.updatedAt }}</div>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </template>
    </template>
    <div v-else class="workflow-loading" aria-busy="true" aria-live="polite">
      <el-skeleton :rows="6" animated />
      <p class="workflow-loading__hint">Loading...</p>
    </div>
  </el-drawer>

  <!-- Sub-drawers / viewers -->
  <WorkflowResponseFormViewer
    v-if="
      workflowFormBeingViewed?.formDefinition &&
      workflowFormBeingViewed.formResponseDto
    "
    :form-definition="workflowFormBeingViewed.formDefinition"
    :incoming-form-response-dto="workflowFormBeingViewed.formResponseDto"
    :form-drawer-open="formDrawerOpen"
    :workflow="workflow"
    :shared-answers="sharedAnswersForProps"
    @close-form-drawer="handleCloseFormDrawer"
  />
  <EntityResponseViewer
    v-if="workflow?.entityGuid && entityDrawerOpen"
    :key="workflow?.entityGuid"
    :entity="workflow?.entity ?? ({} as Entity)"
    @close-drawer="entityDrawerOpen = false"
  />
</template>

<style lang="less">
.workflow-loading {
  padding: 24px;

  &__hint {
    margin-top: 16px;
    text-align: center;
    color: var(--el-text-color-secondary);
    font-size: var(--el-font-size-base);
  }
}

body.fullScreen {
  .el-overlay {
    z-index: 1;
    background: transparent;
    overflow: visible;
    height: auto;
    top: calc(var(--el-header-height) + 24px);
  }
  .el-drawer__header {
    padding: 0 19px 0 23px;
    box-shadow: inset 0px -5px 5px -5px hsla(0, 0%, 0%, 0.15);
  }
  .compliance.mainContainer .homeHeader {
    z-index: 3000;
  }
  .workflow-drawer {
    transition: none;
    top: 40px;
    height: auto;
    box-shadow: none;
    background: transparent;
  }
  .bottom-display {
    position: relative;
    justify-content: right;
    margin: 1em 0 0 0;
    bottom: 0;
    right: 0;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles used in .workflow-drawer and .workflow-inline
// ─────────────────────────────────────────────────────────────────────────────
.workflow-drawer,
.workflow-inline {
  .devOverrides {
    display: flex;
    position: relative;
    gap: 0.5em;
    align-items: center;
    &::before {
      position: absolute;
      content: "Design & Test Only";
      top: -1.5em;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 0.85rem;
      color: var(--el-color-info-light-3);
      border-bottom: 1px dashed var(--el-color-info-light-3);
    }
    > div {
      display: flex;
      gap: 0.2em;
      align-items: center;
    }
  }

  // ── Unified layout ──────────────────────────────────────────────────────────
  .workflow-layout-container {
    display: grid;
    // Sidebar is a fixed-width column; main area takes the rest
    grid-template-columns: 280px 1fr;
    gap: 0.75em;
    align-items: start;
    width: 100%;

    &.sidebar-collapsed {
      grid-template-columns: 48px 1fr;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;

      .workflow-sidebar {
        display: none;
      }
      position: relative;
      bottom: auto;
      right: auto;
      justify-content: center;
      margin-top: 1em;
      padding: 0.5em;
      flex-wrap: wrap;
      gap: 0.5em;

      > span {
        font-size: 0.8em;
      }
    }
  }
  .workflow-info {
    display: flex;
    gap: 1em;

    table {
      width: 100%;
      td {
        padding: 0.5em;
      }
    }

    // Mobile responsiveness
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.5em;

      table {
        font-size: 14px;

        td {
          padding: 0.25em;
        }
      }
    }
  }
  .el-collapse-item + .el-collapse-item {
    margin-top: 0px;
  }
  .el-collapse-item__wrap {
    border-bottom: none;
  }

  // View Mode Selector
  .view-mode-selector {
    margin-bottom: 1em;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: sticky;
    top: 0.1em; // or whatever spacing you want from top

    z-index: 100;
  }

  .previous-versions-bar {
    display: flex;
    justify-content: flex-end;
    padding: 4px 8px;
    margin-bottom: 4px;
  }

  /* When the sidebar is hidden (compare mode not available), make the layout a single column
     so the main area takes the full width.  */
  .workflow-layout-container.no-sidebar {
    grid-template-columns: 1fr;

    .workflow-main-area {
      grid-column: 1 / -1; /* span entire grid to avoid being placed in the sidebar column */
      width: 100%;
      max-width: 100%;

      .workflow-forms-collapse {
        .el-collapse-item {
          .el-collapse-item__wrap {
            /* Remove any artificial max-height/overflow that could cut off content */
            max-height: none;
            overflow: visible;
          }
        }
      }
    }
  }

  .sidebar-create-item {
    padding: 4px 8px;
  }

  // Compare Mode Container
  .compare-mode-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
    gap: 1em;
    min-height: 500px;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }
  }

  // ── Sidebar ────────────────────────────────────────────────────────────────
  .workflow-sidebar {
    background-color: var(--el-fill-color-light);
    border: 1px solid var(--el-border-color);
    border-radius: var(--el-border-radius-base);
    overflow: hidden;
    // Stick to top of scroll area so it doesn't scroll away
    position: sticky;
    top: 2em;
    max-height: calc(100vh - 160px);
    display: flex;
    flex-direction: column;

    &.collapsed {
      .sidebar-content {
        display: none;
      }

      .sidebar-header {
        flex-direction: column;
        align-items: center;
        gap: 0.4em;
        padding: 0.5em 0.25em;
        border-bottom: none;
      }
    }

    .sidebar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.6em 0.75em;
      background-color: var(--el-color-info-light-9);
      border-bottom: 1px solid var(--el-border-color);
      flex-shrink: 0;

      .sidebar-arrow-box {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 25px;
        height: 25px;
        border: 1px solid var(--el-border-color);
        border-radius: 8px;
        cursor: pointer;

        .el-icon {
          font-size: 16px;
        }
      }

      .sidebar-title {
        font-weight: 600;
        color: var(--el-text-color-secondary);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }

      .sidebar-title-vertical {
        writing-mode: vertical-rl;
        text-orientation: mixed;
        font-weight: 600;
        font-size: 0.75em;
        color: var(--el-text-color-secondary);
        text-transform: uppercase;
        letter-spacing: 0.06em;
        cursor: pointer;
        user-select: none;
      }
    }

    .sidebar-separator {
      width: 100%;
      height: 1px;
      background: var(--el-border-color);
      margin: 0.5em 0;
      flex-shrink: 0;
    }

    .sidebar-content {
      padding: 0.4em;
      overflow-y: auto;
      flex: 1;
    }

    .sidebar-form-item {
      padding: 0.25em 0.5em;
      margin-bottom: 0.35em;
      background-color: var(--el-bg-color);
      border: 1px solid var(--el-border-color-light);
      border-radius: var(--el-border-radius-base);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        border-color: var(--el-color-primary-light-5);
        background-color: var(--el-color-primary-light-9);
      }

      // Highlight the form that is currently open/selected
      &.is-active {
        border-color: var(--el-color-primary);
        background-color: var(--el-color-primary-light-8);

        .form-item-title {
          color: var(--el-color-primary-dark-2);
          font-weight: 500;
        }
      }

      .form-item-header {
        display: flex;
        align-items: flex-start;
        gap: 0.4em;

        .form-item-title {
          font-size: 0.9rem;
          color: var(--el-text-color-primary);
        }
      }
    }
  }

  // ── Main content area ──────────────────────────────────────────────────────
  .workflow-main-area {
    min-width: 0; // prevent grid blowout
    width: 100%;

    .workflow-forms-collapse {
      width: 100%;

      // Side-by-side: render up to 2 collapse items in a grid.
      // auto-fit means a single selected form expands to full width.
      &.compare-mode {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
        gap: 0.75em;
        align-items: start;

        .el-collapse-item {
          .el-collapse-item__wrap {
            max-height: 80vh;
            overflow-y: auto;
          }
        }

        // On narrower viewports collapse to single column
        @media (max-width: 1100px) {
          grid-template-columns: 1fr;
        }
      }

      // Stacked: single full-width column (default el-collapse behaviour)
      &.stacked-mode {
        display: block;
      }

      &.stacked-mode .el-collapse-item + .el-collapse-item {
        margin-top: 5px;
      }

      .el-collapse-item__wrap {
        border-bottom: none;
      }
    }
  }

  // ── Collapse item header styling ──────────────────────────────────────────
  .workflow-forms-collapse,
  .el-collapse {
    .el-collapse-item__header {
      font-size: 1.15em;
      padding: 0.3rem 1rem;
      background-color: var(--el-color-info-light-3);
      border-radius: var(--el-border-radius-base);
      color: var(--el-color-info-light-9);
      box-shadow: inset 0 -1.5px 0 rgba(0, 0, 0, 0.1);
      line-height: normal;
      height: fit-content;
      width: auto;
    }

    .el-collapse-item {
      &.is-active {
        .el-collapse-item__header {
          background-color: var(--el-color-primary-dark-3);
          color: var(--el-color-white);
          font-weight: bold;
        }
      }
    }
  }

  // Form title
  .form-title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .form-title {
      flex: 1;
    }

    .form-status {
      font-size: 0.85em;
      font-weight: normal;
      margin-right: 1em;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5em;

      .form-status {
        margin-right: 0;
        align-self: flex-end;
      }
    }
  }

  // Hide arrow when single form
  .single-form {
    .el-collapse-item__arrow {
      display: none;
    }
    .form-status {
      margin-right: 0;
    }
  }

  a.noClick,
  .noClick a {
    cursor: text;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer-specific styles
// ─────────────────────────────────────────────────────────────────────────────
.workflow-drawer {
  .el-drawer__body {
    overflow-y: auto;
    padding-top: 0;
  }

  .el-drawer__header {
    .el-drawer__title {
      display: flex;
      align-items: center;
      .el-input__inner {
        font-size: 1.2rem;
      }

      .workflow-info {
        display: flex;
        align-items: center;
      }

      &.withEntity {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-right: 11px;
        .entity-info {
          margin-left: 15px;
          cursor: pointer;
          font-size: 1.5rem;
        }
      }
    }
  }

  .form {
    margin-top: 0.5em;
    .viewersBar {
      padding-bottom: 0;
    }
  }

  .bottom-display {
    bottom: 1em;
    right: 1em;
    color: var(--color-text-muted);
    display: flex;
    gap: 1em;
    align-items: center;
    margin-top: 1em;
    > span {
      font-size: 0.7em;
    }

    @media (max-width: 768px) {
      position: relative;
      bottom: auto;
      right: auto;
      justify-content: center;
      padding: 0.5em;
      flex-wrap: wrap;
      gap: 0.5em;
      > span {
        font-size: 0.8em;
      }
    }
  }

  .workflow-info {
    display: flex;
    gap: 1em;

    table {
      width: 100%;
      td {
        padding: 0.5em;
      }
    }

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.5em;
      table {
        font-size: 14px;
        td {
          padding: 0.25em;
        }
      }
    }
  }

  .summary-section {
    display: grid;
    grid-template-columns: auto auto;

    .summary-info {
      display: flex;
      flex-direction: row;
      column-gap: 0.5em;
      flex-wrap: wrap;
      margin-bottom: 1em;
      .summary-info-row {
        display: flex;
        align-items: center;
        column-gap: 0.5em;
        flex-wrap: wrap;
        text-wrap: nowrap;
        .sub-header {
          font-weight: 600;
        }
      }
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1em;
      .summary-info {
        .summary-info-row {
          flex-direction: column;
          align-items: flex-start;
          text-wrap: wrap;
        }
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline-specific styles
// ─────────────────────────────────────────────────────────────────────────────
.workflow-inline {
  .inline-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    background: var(--el-color-primary-light-9);
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 6px;
    margin-bottom: 8px;
    gap: 12px;
  }

  .inline-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  .inline-header-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--el-text-color-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .inline-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;

    .entity-info {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.8rem;
      color: var(--el-color-primary);
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        text-decoration: underline;
      }

      i {
        font-size: 1rem;
      }
    }
  }

  .form {
    margin-top: 0.5em;
    .viewersBar {
      padding-bottom: 0;
    }
  }

  .el-collapse-item {
    .el-collapse-item__wrap {
      .el-collapse-item__content {
        padding: 0 1em;
      }
    }
  }

  .bottom-display {
    color: var(--color-text-muted);
    display: flex;
    gap: 1em;
    align-items: center;
    margin-top: 1em;
    > span {
      font-size: 0.7em;
    }
  }
}
</style>
